import { useHasModule, useIsVectorCore } from 'api';
import { useGetZendeskAuth } from 'api/zendesk/get';
import * as React from 'react';

const ARTICLES = {
    newVector8: 'https://support.catapultsports.com/hc/en-us/articles/14358733552783-Vector-8-and-OpenField-Onboarding',
    newVector7: 'https://support.catapultsports.com/hc/en-us/articles/15168677261327-Vector-7-and-OpenField-Onboarding',
    upgrade: 'https://support.catapultsports.com/hc/en-us/articles/15841882976783-Vector-7-to-Vector-8-Upgrade',
};

export interface OnboardingDetails {
    zendesk_id?: string | null;
    admin_of_user_id?: string | null;
    vector_onboarding_type?: string | null;
}

// A blank type is treated as 'new'
function getArticle(type?: string | null, isVector8?: boolean | null): string | null {
    if (type === 'upgrade') {
        return ARTICLES.upgrade;
    }

    if ((!type || type === 'new') && isVector8 !== null && isVector8 !== undefined) {
        return isVector8 ? ARTICLES.newVector8 : ARTICLES.newVector7;
    }

    return null;
}

export function buildOnboardingUrl(details?: OnboardingDetails, isVector8?: boolean | null): string | null {
    const article = getArticle(details?.vector_onboarding_type, isVector8);

    if (!article || !details?.zendesk_id) {
        return null;
    }

    const params = new URLSearchParams({ userId: details.zendesk_id });

    // blank is a data quality issue on the Zendesk record; the onboarding app falls back to admin level
    if (details.admin_of_user_id && details.admin_of_user_id !== 'null') {
        params.set('adminOfUserId', details.admin_of_user_id);
    }

    return `${article}?${params.toString()}`;
}

interface UseOnboarding {
    isEnabled: boolean | null;
    url: string | null;
    type: string | null;
    open: () => boolean;
}

export function useOnboarding(): UseOnboarding {
    const isVectorCore = useIsVectorCore();
    const isVector8 = useHasModule('VectorHybrid');
    const isEnabled = isVectorCore === null ? null : !isVectorCore;
    const authResponse = useGetZendeskAuth();

    const details = authResponse?.data?.details as OnboardingDetails | undefined;

    const url = React.useMemo(
        () => (isEnabled === true ? buildOnboardingUrl(details, isVector8) : null),
        [details, isEnabled, isVector8],
    );

    const open = React.useCallback(() => {
        const tab = url ? window.open(url, '_blank') : null;

        if (tab) {
            tab.opener = null;
        }

        return !!tab;
    }, [url]);

    return { isEnabled, url, type: details?.vector_onboarding_type ?? null, open };
}
